import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, CheckCircle2, Gift, PhoneCall, X } from "lucide-react";
import { submitSpecialOffer } from "../../services/specialOfferService";
import { offerData } from "./offerData";
import offerPromoImg from "../../assets/special-offer/offer-yellow-card.webp";
import whySymImg from "../../assets/special-offer/why-sym-machine.webp";
import "./SpecialOfferPopup.css";

const EMPTY_FORM = {
  name: "", email: "", phone: "", machine: "", postcode: "",
  previousPurchase: "", consent: false, website: "",
};

export default function SpecialOfferPopup() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("promo");
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const closeButtonRef = useRef(null);
  const triggerRef = useRef(null);

  // Automatically show once per browser tab, unless dismissed earlier.
  useEffect(() => {
    if (sessionStorage.getItem("sym-offer-dismissed") === "1") return;
    const timer = window.setTimeout(() => setOpen(true), 1200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        sessionStorage.setItem("sym-offer-dismissed", "1");
        triggerRef.current?.focus();
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(document.querySelectorAll(
        ".sym-popup button:not([disabled]), .sym-popup a[href], .sym-popup input:not([disabled]):not([tabindex='-1']), .sym-popup select:not([disabled])"
      )).filter((element) => element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const dismiss = () => {
    sessionStorage.setItem("sym-offer-dismissed", "1");
    setOpen(false);
    triggerRef.current?.focus();
  };
  const show = () => {
    setStep("promo");
    setError("");
    setOpen(true);
  };
  const update = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    try {
      const response = await submitSpecialOffer({
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        postcode: form.postcode.trim(),
      });
      if (response.data?.success === false) {
        throw new Error(response.data.message || "Submission failed");
      }
      sessionStorage.setItem("sym-offer-dismissed", "1");
      setStep("success");
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Could not send your enquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          ref={triggerRef}
          type="button"
          className="sym-pop-tab"
          onClick={show}
          aria-label="Claim SYM special offer"
        >
          <Gift size={18} aria-hidden="true" />
          <span>CLAIM OFFER</span>
        </button>
      )}
      {open && createPortal(
        <div className="sym-popup-overlay" onMouseDown={(event) => {
          if (event.target === event.currentTarget) dismiss();
        }}>
          <section className="sym-popup" role="dialog" aria-modal="true" aria-labelledby="sym-offer-dialog-title">
            <button
              ref={closeButtonRef}
              className="sym-popup-close"
              type="button"
              onClick={dismiss}
              aria-label="Close special offer"
            >
              <X size={20} aria-hidden="true" />
            </button>

            <div className={`sym-popup-layout ${step !== "promo" ? "sym-popup-layout--form" : ""}`}>
              <div className="sym-popup-left">
                <img
                  className="sym-popup-main-image"
                  src={offerPromoImg}
                  alt="SYM Machinery: save up to $15,000 on your first eligible TerrainX35 or Titan35"
                />
                <div className="sym-popup-mobile-head" aria-hidden="true">
                  <span>SYM MACHINERY • FIRST PURCHASE</span>
                  <strong>Save up to $15,000*</strong>
                  <small>TerrainX35 or Titan35</small>
                </div>
              </div>

              <div className="sym-popup-right">
                {step === "promo" && (
                  <>
                    <div className="sym-popup-proof" aria-label="Why SYM is better">
                      <img
                        src={whySymImg}
                        alt="SYM TerrainX35: 42mm steel front end, Australian-plated and rated, waterproof plugs and hand-picked turbo diesel"
                      />
                    </div>
                    <div className="sym-popup-content">
                      <span className="sym-popup-kicker">EXCLUSIVE FIRST-PURCHASE OFFER</span>
                      <h2 id="sym-offer-dialog-title">Your next SYM machine could cost <em>$15,000 less.*</em></h2>
                      <p>Explore the TerrainX35 and Titan35. Ask the SYM team about current sale pricing and eligibility.</p>
                      <div className="sym-popup-benefits">
                        <span>✓ Selected SYM machines</span>
                        <span>✓ No-obligation enquiry</span>
                      </div>
                      <button type="button" className="sym-popup-primary" onClick={() => setStep("form")}>
                        CLAIM MY OFFER <ArrowRight size={18} aria-hidden="true" />
                      </button>
                      <p className="sym-popup-fine">*Savings up to $15,000. First purchases of eligible models only. Limited time; conditions apply. SYM confirms pricing and eligibility.</p>
                    </div>
                  </>
                )}

                {step === "form" && (
                  <div className="sym-popup-form-wrap">
                    <span className="sym-popup-kicker">REQUEST YOUR SALE PRICING</span>
                    <h2 id="sym-offer-dialog-title">Claim your special offer</h2>
                    <p>Enter your details and a SYM team member will contact you.</p>
                    <form onSubmit={submit} className="sym-popup-form">
                      <div className="sym-popup-fields">
                        <label>Full name *<input autoComplete="name" required minLength={2} maxLength={120} value={form.name} onChange={(e) => update("name", e.target.value)} /></label>
                        <label>Email address *<input type="email" autoComplete="email" required maxLength={254} value={form.email} onChange={(e) => update("email", e.target.value)} /></label>
                        <label>Mobile number *<input type="tel" autoComplete="tel" required maxLength={22} value={form.phone} onChange={(e) => update("phone", e.target.value)} /></label>
                        <label>Machine *<select required value={form.machine} onChange={(e) => update("machine", e.target.value)}><option value="">Select a machine</option><option value="TerrainX35">TerrainX35</option><option value="Titan35">Titan35</option><option value="Not sure">Not sure</option></select></label>
                        <label className="sym-popup-field-wide">Australian postcode *<input autoComplete="postal-code" inputMode="numeric" required pattern="[0-9]{4}" maxLength={4} placeholder="4 digits" value={form.postcode} onChange={(e) => update("postcode", e.target.value)} /></label>
                      </div>
                      <fieldset className="sym-popup-radio-group"><legend>Have you bought from SYM before? *</legend>
                        <label><input type="radio" name="symOfferPreviousPurchase" required checked={form.previousPurchase === "Yes"} onChange={() => update("previousPurchase", "Yes")} /> Yes</label>
                        <label><input type="radio" name="symOfferPreviousPurchase" required checked={form.previousPurchase === "No"} onChange={() => update("previousPurchase", "No")} /> No</label>
                      </fieldset>
                      <label className="sym-popup-consent"><input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} required /> I consent to SYM Machinery contacting me about this enquiry. *</label>
                      <div className="sym-popup-honeypot" aria-hidden="true"><label>Website<input type="text" autoComplete="off" tabIndex={-1} value={form.website} onChange={(e) => update("website", e.target.value)} /></label></div>
                      {error && <p className="sym-popup-error" role="alert">{error}</p>}
                      <button disabled={loading} type="submit" className="sym-popup-primary">{loading ? "SENDING…" : "GET MY SALE PRICING"} <ArrowRight size={18} aria-hidden="true" /></button>
                      <button type="button" className="sym-popup-back" onClick={() => setStep("promo")}>← Back to offer</button>
                    </form>
                  </div>
                )}

                {step === "success" && (
                  <div className="sym-popup-thankyou" role="status">
                    <CheckCircle2 size={46} color="#178348" aria-hidden="true" />
                    <h2 id="sym-offer-dialog-title">Enquiry received!</h2>
                    <p>Thanks! The SYM team will contact you about the current offer.</p>
                    <a href={offerData.phoneHref} className="sym-popup-call"><PhoneCall size={17} /> {offerData.phoneDisplay}</a>
                    <button type="button" className="sym-popup-primary" onClick={dismiss}>CONTINUE BROWSING</button>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>,
        document.body
      )}
    </>
  );
}
